import { EWinPresentationStates } from '@Core/Win/States/WinPresentationStates';
import { Game } from '@Game/Game';
import { GameHistory } from '@GameHistory/GameHistory';
import { EGameStoreKeys } from '@GameStore/GameStoreSchema';
import { ETurboModeLevels, IAutoPlayState } from '@GFTypes/GameStoreTypes/SpinParameters';
import { EActionName } from '@Managers/Events/EActionName';
import EventsCenter from '@Managers/Events/EventsCenter';
import { EGameEvents } from '@Managers/Events/GameEvents';
import { EWinType } from '@Managers/Redux/HttpState';
import { EGameStates } from '@Managers/StateManager/GameStates';
import { StateConditions } from '@Managers/StateManager/StateConditions';
import { getCurrentTurboLevel } from '@Managers/TurboManager/TurboManager';

export const betDecreaseButtonAction = (game: Game): void => {
    game.betManager.descrease();
};

export const betIncreaseButtonAction = (game: Game): void => {
    game.betManager.increase();
};

export const betIndexButtonAction = (game: Game, index: number): void => {
    game.betManager.setIndex(index);
};

export const spinButtonAction = (game: Game): void => {
    if (!game.gameStore.get(EGameStoreKeys.ButtonState).isSpinButtonActive) return;
    const { win } = game.gameStore.get(EGameStoreKeys.SpinOutcome);
    const isHistoryMode = game.gameStore.get(EGameStoreKeys.GameHistoryState).isHistoryMode;
    const isPauseInitiated = game.gameStore.get(EGameStoreKeys.IsPauseInitiated);
    const canWagerSmallestBet = game.gameStore.get(EGameStoreKeys.BetState).available || game.betManager.getCurrentSpinBet() === 0;

    if (win && !isHistoryMode) takeAction(game, true);

    if (!isPauseInitiated && canWagerSmallestBet) StateConditions.idleCondition.resolve();
};

export const stopButtonAction = (game: Game): void => {
    const isTurboMode = game.gameStore.get(EGameStoreKeys.TurboModeState).active && getCurrentTurboLevel(game.gameStore) === ETurboModeLevels.TURBO;
    if (!game.gameStore.get(EGameStoreKeys.ButtonState).isStopButtonActive) return;
    if (game.gameStore.get(EGameStoreKeys.IsQuickStopInitiated) || isTurboMode) EventsCenter.emit(EGameEvents.SKIP_SHORTS);
    if (isTurboMode) return;
    game.gameStore.set(EGameStoreKeys.IsQuickStopInitiated, true);
    EventsCenter.emit(EGameEvents.QUICK_STOP);
};

export const skipButtonAction = (game: Game): void => {
    if (!game.gameStore.get(EGameStoreKeys.ButtonState).isSkipButtonActive) return;
    const currentState = game.gameStore.get(EGameStoreKeys.CurrentGameState);
    const { currentState: currentWinPresentationState } = game.gameStore.get(EGameStoreKeys.WinPresentation);
    const { finalWinLevelName } = game.gameStore.get(EGameStoreKeys.BaseWinPresentationState);
    const isBigWin = finalWinLevelName !== EWinType.NORMAL_WIN;
    const isWinVisible = currentWinPresentationState !== EWinPresentationStates.WIN_PRESENTATION_HIDDEN_STATE;
    const isPostWinState = currentState === EGameStates.POST_WIN_STATE;
    const isWinState = currentState === EGameStates.WIN_STATE;
    EventsCenter.emit(EGameEvents.SKIP);
    if (isWinState) return;
    if (isPostWinState && isBigWin && isWinVisible) takeAction(game);
};

export const continueButtonAction = (game: Game): void => {
    if (!game.gameStore.get(EGameStoreKeys.ButtonState).isContinueButtonActive) return;
    takeAction(game, false);
    EventsCenter.emit(EGameEvents.CONTINUE);
};

export const takeButtonAction = (game: Game): void => {
    if (game.gameStore.get(EGameStoreKeys.GameHistoryState).isHistoryMode) {
        setTimeout(() => {
            EventsCenter.emit(EGameEvents.ACTION_RESPONSE_RECEIVED, undefined);
        }, GameHistory.RESPONSE_TAKE_TIME);
        game.gameStore.set(EGameStoreKeys.IsTakeInitiated, true);
        EventsCenter.emit(EGameEvents.TAKE);
    } else {
        if (!game.gameStore.get(EGameStoreKeys.ButtonState).isTakeButtonActive) return;
        const currentState = game.gameStore.get(EGameStoreKeys.CurrentGameState);
        if (![EGameStates.WIN_STATE, EGameStates.POST_WIN_STATE, EGameStates.GAMBLE_STATE, EGameStates.POST_WIN_FEATURES_STATE].includes(currentState)) return;
        takeAction(game);
    }
};

export const autoStartButtonAction = (game: Game, settings: IAutoPlayState): void => {
    if (!game.gameStore.get(EGameStoreKeys.ButtonState).isAutoButtonActive) return;

    EventsCenter.emit(EGameEvents.AUTOPLAY_MODE_START);
    game.gameStore.set(EGameStoreKeys.AutoPlayState, { isEnabled: true, currentCount: settings.totalCount, totalCount: settings.totalCount }, true);

    game.autoPlayManager.onIdleStart();
};

export const autoStopButtonAction = (game: Game): void => {
    if (!game.gameStore.get(EGameStoreKeys.ButtonState).isAutoButtonActive) return;

    game.autoPlayManager.stopAutoPlay();
};

export const gambleButtonAction = (game: Game): void => {
    if (!game.gameStore.get(EGameStoreKeys.ButtonState).isGambleButtonActive) return;
    const currentState = game.gameStore.get(EGameStoreKeys.CurrentGameState);
    const isTakeInitiated = game.gameStore.get(EGameStoreKeys.IsTakeInitiated);

    if (![EGameStates.WIN_STATE, EGameStates.POST_WIN_STATE].includes(currentState)) return;

    if (currentState === EGameStates.WIN_STATE) {
        game.gameStore.set(EGameStoreKeys.GambleState, { gambleRequested: true }, true);
        StateConditions.winCondition.resolve();
        StateConditions.postWinCondition.resolve();
    } else if (currentState === EGameStates.POST_WIN_STATE && !isTakeInitiated) {
        game.gameStore.set(EGameStoreKeys.GambleState, { gambleRequested: true }, true);
        StateConditions.postWinCondition.resolve();
    }
};

export const turboButtonAction = (game: Game, autoMode: boolean = false, options?: { level: number }): void => {
    const { baseGameLevel, specialGameLevel } = game.gameStore.get(EGameStoreKeys.TurboModeState);
    if (specialGameLevel) return;
    let nextLevel;

    if (options) {
        nextLevel = options.level;
    } else {
        nextLevel = baseGameLevel === ETurboModeLevels.TURBO ? ETurboModeLevels.NORMAL : autoMode ? ETurboModeLevels.TURBO : baseGameLevel + 1;
    }

    game.gameStore.set(EGameStoreKeys.TurboModeState, { active: nextLevel > 0, baseGameLevel: nextLevel }, true);
};

export const muteButtonAction = (game: Game): void => {
    if (game.gameStore.get(EGameStoreKeys.ButtonState).isMuteButtonActive) EventsCenter.emit(EGameEvents.MUTE);
};

export const unmuteButtonAction = (game: Game): void => {
    if (game.gameStore.get(EGameStoreKeys.ButtonState).isUnmuteButtonActive) EventsCenter.emit(EGameEvents.UNMUTE);
};

export const fullscreenButtonAction = (game: Game): void => {
    if (!game.gameStore.get(EGameStoreKeys.ButtonState).isFullscreenButtonActive) return;

    if (game.gameStore.get(EGameStoreKeys.IsFullscreen)) EventsCenter.emit(EGameEvents.EXIT_FULLSCREEN);
    else EventsCenter.emit(EGameEvents.FULLSCREEN);
};

export const takeAction = (game: Game, keepWinPresentation = false): void => {
    const isTakeInitiated = game.gameStore.get(EGameStoreKeys.IsTakeInitiated);
    const canPerformTake = game.gameStore.get(EGameStoreKeys.NextActions).includes(EActionName.TAKE);
    const { active: isDebugWinPresentationModeActive } = game.gameStore.get(EGameStoreKeys.DebugWinPresentationModeState);
    if (isTakeInitiated || (!canPerformTake && !isDebugWinPresentationModeActive)) return;
    game.gameStore.set(EGameStoreKeys.IsTakeInitiated, true);
    if (!isDebugWinPresentationModeActive) EventsCenter.emit(EGameEvents.SEND_ACTION_REQUEST, { actionName: EActionName.TAKE });
    EventsCenter.emit(EGameEvents.TAKE, { keepWinPresentation });
};
